import React, { useState, useEffect, useRef } from "react";
import { useFormik } from "formik";
import { classNames } from "primereact/utils";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { Dropdown } from "primereact/dropdown";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { selectData } from "../../services/store/userSlice";
import { FileUpload } from "primereact/fileupload";
import { Dialog } from "primereact/dialog";
import { calcEndDate, LeaveStatus } from "../../common/util/constants";
import { leaveService } from "../../services/extensions";
import { Util } from "../../services";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { InputText } from "primereact/inputtext";
import { Draft } from "./views";
import { attendanceApi } from "../../services";
import { generateRandomColor } from "./controllers/employee-controller";
import "./styles/style.scss";

const NewAbsenceRequest = ({
  back,
  leaves,
  reload,
  dialog = false,
  start = null,
  end = null,
}) => {
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const userLeaveData = useSelector(selectData);
  const { user } = useSelector((state) => state.user);
  const [disabled, setDisabled] = useState(false);
  const [serverMsg, setServerMsg] = useState(null);
  const [fileObjArray, setFileObjArray] = useState([]);
  const [missing, setMissing] = useState([]);
  const [showMissing, setShowMissing] = useState(false);
  const [respLeave, setRespLeave] = useState([]);
  const [region, setRigion] = useState("ESIN");
  const leaveReasons = {
    ML: ["Delivery", "Adoption", "Miscarriage", "Illness"],
    PL: ["Delivery", "Adoption"],
  };
  const chilNumbers = { Zero: 0, One: 1, Two: 2, Three: 3, Four: 4 };
  const numList = ["Zero", "One", "Two", "Three", "Four"];
  const [absenceCodes, setAbsenceCodes] = useState([]);
  const [leaveDuration, setLeaveDuration] = useState([]);
  const [disableEndDate, setDisableEndDate] = useState(false);
  const AU_HOURLY_LEAVE = { code: "HR", title: "Hourly", label: "Hourly" };

  useEffect(() => {
    if (user) {
      const leCode = user?.LegalEntity?.code || "ESIN";
      setRigion(leCode);
      // setRigion("ESAU");
    }
  }, [user]);

  useEffect(() => {
    if (dialog && start && end) {
      formik.setFieldValue("startDate", start);
      formik.setFieldValue("endDate", end);
    }
  }, [dialog, start, end]);

  useEffect(() => {
    if (region === "ESAU") {
      setLeaveDuration([AU_HOURLY_LEAVE]);
      formik.setFieldValue("leaveType", AU_HOURLY_LEAVE);
    } else if (
      region === "ESSG" &&
      userLeaveData?.userLeaveType &&
      userLeaveData.userLeaveType.length > 0
    ) {
      // For Singapore region, initialize with available leave types (HD and FD)
      setLeaveDuration(userLeaveData.userLeaveType);
    }
  }, [region, userLeaveData]);

  useEffect(() => {
    if (userLeaveData?.userAbsenceCodes) {
      let serializedAbsenceCodes = [];
      userLeaveData.userAbsenceCodes.map((code) => {
        if (code.status === 1) {
          serializedAbsenceCodes.push({
            ...code,
            label: typeof code.label === "string" ? code.label : code.label,
          });
        }
      });
      setAbsenceCodes(serializedAbsenceCodes);
    }
  }, [userLeaveData]);

  useEffect(() => {
    getMissingAttendence();
  }, []);

  const formik = useFormik({
    initialValues: {
      startDate: null,
      endDate: null,
      absenceCode: "",
      leaveType: "",
      leaveHours: "",
      reasonOfLeave: "",
      reason: "",
      status: LeaveStatus.DRAFT,
      recallReason: "",
      isAttendance: false,
      attachment: null,
      dateOfDelivery: null,
      numberOfChild: "Zero",
      isChildAdopted: false,
      adoptionDate: null,
      adoptedChildAge: 0,
      isMiscarriage: false,
      miscarriageDate: null,
      isIllness: false,
      expirationDate: null,
      childCitizenship: "", // "SG" or "Non-SG"
      childDateOfBirth: null,
    },
    validate: (data) => {
      let errors = {};
      if (data.startDate === null) {
        errors.startDate = "Start Date is required.";
      }
      if (["ESIN", "ESSG"].includes(region)) {
        if (!data.endDate && data.absenceCode !== "ML") {
          errors.endDate = "End Date is required.";
        }
      } else {
        if (!data.endDate) {
          errors.endDate = "End Date is required.";
        }
      }

      if (!data.absenceCode) {
        errors.absenceCode = "Absence Code is required.";
      }
      if (!data.leaveType) {
        errors.leaveType = "Leave Duration is required.";
      }
      if (!data.reasonOfLeave) {
        errors.reasonOfLeave = "Reason is required.";
      }
      if (
        ["PL", "BL", "LWP", "WFH", "WFO", "ML", "WHO"].indexOf(
          data.absenceCode.code,
        ) >= 0
      ) {
        if (["ESIN"].includes(region)) {
          if (
            !data.dateOfDelivery &&
            ["ML", "PL"].includes(data.absenceCode.code) &&
            data.leaveReason === "Delivery"
          ) {
            errors.dateOfDelivery = "Date of delivery is required.";
          }
          if (
            !data.leaveReason &&
            ["ML", "PL"].includes(data.absenceCode.code)
          ) {
            errors.leaveReason = "Leave reason is required.";
          }
        } else {
          if (!data.reasonOfLeave) {
            errors.reasonOfLeave = "Reason of leave is required.";
          }
        }
      } else {
        let balReq = false;
        switch (region) {
          case "ESIN":
            balReq = true;
            break;
          case "ESSG":
            balReq = true;
            break;
          case "ESUK":
            balReq = false;
            break;
          default:
            balReq = true;
            break;
        }
        if (balReq && data.absenceCode.balance == 0) {
          errors.absenceCode = "You don't have balance in this asbsence code";
        }
      }
      if (["ESSG"].includes(region) && data.absenceCode.code === "CCL") {
        if (!data.childCitizenship) {
          errors.childCitizenship = "Child citizenship is required.";
        }
        if (
          data.childCitizenship === "Singapore Citizen" &&
          !data.childDateOfBirth
        ) {
          errors.childDateOfBirth = "Child date of birth is required.";
        }
      }

      // Australia – Hourly leave validation
      if (region === "ESAU" && data.leaveType?.code === "HR") {
        if (!data.leaveHours && data.leaveHours !== 0) {
          errors.leaveHours = "Leave hours are required.";
        } else if (Number(data.leaveHours) <= 0) {
          errors.leaveHours = "Leave hours must be greater than 0.";
        }
      }
      return errors;
    },
    onSubmit: (data) => {
      let dataObj = {
        startDate: Util.dateConverterForLeave(data.startDate),
        endDate: Util.dateConverterForLeave(data.endDate),
        absenceCode: data.absenceCode.code,
        leaveType: data.leaveType?.code,
        leaveHours:
          region === "ESAU" && data.leaveType?.code === "HR"
            ? Number(data.leaveHours)
            : null,
        reasonOfLeave: data.reasonOfLeave,
        status: LeaveStatus.DRAFT,
        isAttendance: false,
        attachment: null,
        expectedDeliveryDate:
          ["Delivery"].includes(data.leaveReason) && data.dateOfDelivery
            ? Util.dateConverterForLeave(data.dateOfDelivery)
            : null,
        numberOfChild: ["Delivery"].includes(data.leaveReason)
          ? chilNumbers[data.numberOfChild]
          : null,

        adoptedChildAge: parseInt(data.adoptedChildAge),
        isChildAdopted: ["Adoption"].includes(data.leaveReason),
        adoptionDate:
          ["Adoption"].includes(data.leaveReason) && data.adoptionDate
            ? Util.dateConverterForLeave(data.adoptionDate)
            : null,

        isMiscarriage: ["Miscarriage"].includes(data.leaveReason),
        miscarriageDate:
          ["Miscarriage", "Illness"].includes(data.leaveReason) &&
          data.miscarriageDate
            ? Util.dateConverterForLeave(data.miscarriageDate)
            : null,

        isIllness: ["Illness"].includes(data.leaveReason),
        illnessDate:
          ["Illness"].includes(data.leaveReason) && data.miscarriageDate
            ? Util.dateConverterForLeave(data.miscarriageDate)
            : null,
        reason: "",
        recallReason: "",

        expirationDate: data.expirationDate
          ? Util.dateConverterForLeave(data.expirationDate)
          : null,

        childCitizenship: data.childCitizenship || null,
        childDateOfBirth: data.childDateOfBirth
          ? Util.dateConverterForLeave(data.childDateOfBirth)
          : null,
      };
      createNewLeave(dataObj);
    },
  });

  useEffect(() => {
    if (!formik) {
      const absCode = formik?.values?.absenceCode?.value;
    }
  }, [formik]);

  const getMissingAttendence = () => {
    attendanceApi.missingAttendance({}).then((resp) => {
      setMissing(resp.data.leaveDay);
    });
  };
  const uploadFiles = async (files) => {
    return new Promise((resolve, reject) => {
      try {
        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
          formData.append("file", files[i]);
        }
        leaveService
          .uploadAttachment(formData)
          .then(
            (resp) => {
              const attachment = resp?.data?.data;
              if (typeof attachment === "object") {
                const fileArr = attachment.map((x) => x.fileName);
                resolve(fileArr.join(","));
              } else {
                resolve(null);
              }
            },
            (err) => {
              // console.log("Error on uploadig files", err);
            },
          )
          .catch((e) => {
            // console.log("Error on uploadig files", e);
            reject(null);
          });
        // leaveService.uploadAttachment()
      } catch (error) {}
    });
  };

  const createNewLeave = async (dataObj) => {
    const dateA = moment(dataObj.startDate);
    const dateB = moment(dataObj.endDate);
    const days = dateB.diff(dateA, "days");
    const leagalEntity = user["LegalEntity"]["code"] || "ESIN";
    const reqDocsUK = ["ML", "ADL", "BL", "PBL", "PAL", "PL", "SSL"];
    const reqDocsIN = ["ML", "PL"];
    const reqDocsSG = ["ML", "PL", "HL"];
    let reqDocs = [];
    switch (leagalEntity) {
      case "ESUK":
        reqDocs = reqDocsUK;
        break;
      case "ESIN":
        reqDocs = reqDocsIN;
        break;
      case "ESSG":
        reqDocs = reqDocsSG;
        break;
      default:
        reqDocs = [];
    }
    // Add validation for SL - check if >2 consecutive days
    // Singapore SL validation - only require document if >2 consecutive days
    if (leagalEntity === "ESSG" && dataObj.absenceCode === "SL") {
      const sickLeaveDays = dateB.diff(dateA, "days") + 1;
      if (sickLeaveDays > 2 && fileObjArray.length === 0) {
        toast.error(
          "Medical certificate is required for sick leave more than 2 consecutive days",
        );
        return;
      }
    }
    if (reqDocs.includes(dataObj.absenceCode) && fileObjArray.length === 0) {
      toast.error("Document upload is required for " + dataObj.absenceCode);
      return;
    }
    if (fileObjArray && fileObjArray.length > 0) {
      try {
        dataObj.attachment = await uploadFiles(fileObjArray);
      } catch (error) {
        toast.error("Failed to upload attachments");
        return;
      }
    }
    try {
      leaveService.createLeave(dataObj).then(
        (resp) => {
          toast.success("Request applied on LMS for " + dataObj.startDate);
          localStorage.removeItem("absenceData");
          setRespLeave(resp.data.data);
          setServerMsg(resp?.data?.message);
          setTimeout(() => {
            setServerMsg(null);
          }, 10000);
          formik.resetForm();
          reload();
          setFileObjArray([]);
          fileRef.current.clear();
        },
        (err) => {
          toast.error(err.response.data.error);
          setServerMsg(err.response.data.error);
        },
      );
    } catch (error) {
      toast.error("Unable to add new leave request");
    }
  };

  const isFormFieldValid = (name) =>
    !!(formik.touched[name] && formik.errors[name]);
  const getFormErrorMessage = (name) => {
    return (
      isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    );
  };

  const handleDateChange = (date, abs = null) => {
    formik.setFieldValue("startDate", date);
    const absence = abs || formik.values.absenceCode;
    const leCode = user?.LegalEntity?.code || "ESIN";
    console.log("absence code", absence.code, "legal Code", leCode);
    let isDisable = false;
    switch (leCode) {
      case "ESUK":
        const calculatedDate = calcEndDate[leCode][absence.code];
        if (calculatedDate) {
          const eDate = calculatedDate(date).format("YYYY-MM-DD");
          formik.setFieldValue("endDate", new Date(eDate));
        } else {
          formik.setFieldValue("endDate", null);
        }
        break;
      case "ESIN":
      case "ESSG":
        if (["ML"].includes(absence.code)) {
          formik.setFieldValue("endDate", null);
          isDisable = false;
        } else {
          formik.setFieldValue("endDate", null);
          isDisable = false;
        }
        break;
      default:
        if (["ML"].includes(absence.code)) {
          formik.setFieldValue("endDate", null);
        } else {
          formik.setFieldValue("endDate", null);
        }
    }
    setDisableEndDate(isDisable);
  };

  const changeAbsenceCode = (e) => {
    const absence = e.value;
    formik.handleChange(e);
    formik.setFieldValue("leaveReason", "");
    console.log("Region:", region);
    console.log("Absence Code:", absence.code);
    console.log("userLeaveData.userLeaveType:", userLeaveData.userLeaveType);
    setTimeout(() => {
      handleDateChange(formik.values.startDate, absence);
    }, 100);
    // Australia – allow all leave durations (FD / HD / HR)
    if (region === "ESAU") {
      setLeaveDuration([AU_HOURLY_LEAVE]);
      formik.setFieldValue("leaveType", AU_HOURLY_LEAVE);
      return;
    }
    // Singapore (ESSG) – ALL leave types allow both Half Day and Full Day
    // Singapore (ESSG)
    if (region === "ESSG") {
      if (absence.code === "HL") {
        // Hospitalization Leave - Full Day only
        const fullDay = userLeaveData.userLeaveType.find(
          (x) => x.code === "FD",
        );

        setLeaveDuration([fullDay]);
        formik.setFieldValue("leaveType", fullDay);
      } else {
        // All other leave types - Half Day & Full Day
        setLeaveDuration(userLeaveData.userLeaveType);
      }

      return;
    }
    // Leave duration changes
    if (
      ["ML", "PL", "BL", "RH", "LWP", "ADL", "PBL", "PAL", "SSL"].includes(
        absence.code,
      )
    ) {
      setLeaveDuration(
        userLeaveData.userLeaveType.filter((x) => x.code !== "HD"),
      );
      formik.setFieldValue(
        "leaveType",
        userLeaveData.userLeaveType.find((x) => x.code === "FD"),
      );
      if (["ML", "PL"].includes(absence.code)) {
        setTimeout(() => {
          try {
            formik.setFieldValue(
              "leaveReason",
              leaveReasons[formik.values?.absenceCode?.code][0],
            );
          } catch (error) {
            //
          }
        }, 3000);
      }
    } else {
      console.log("Setting leaveDuration to:", userLeaveData.userLeaveType);
      setLeaveDuration(userLeaveData.userLeaveType);
    }
  };

  const renderNewLeaveRequest = () => {
    return (
      <>
        <div className="formgrid grid">
          <div className="field col-4 mt-2">
            <strong className="ml-0">Manager Name: </strong>
          </div>
          <div className="field col-8">
            <span className="p-mt-4 p-input-icon-right">
              <span className="p-float-label">
                <div className="p-inputtext p-component p-filled initCap">
                  {user.superior
                    ? user?.superior?.firstName + " " + user?.superior?.lastName
                    : "No Manager"}
                </div>
              </span>
            </span>
          </div>
        </div>
        <div className="formgrid grid">
          <div className="field col-4 mt-2">
            <strong className="ml-0">Absence Code: </strong>
          </div>
          <div className="field col-8">
            <span className="p-mt-4 p-input-icon-right">
              <span className="p-float-label">
                <Dropdown
                  id="absenceCode"
                  name="absenceCode"
                  placeholder="Select Absence code"
                  value={formik.values.absenceCode}
                  options={absenceCodes}
                  optionLabel="label"
                  onChange={changeAbsenceCode}
                  className={classNames({
                    "p-invalid": isFormFieldValid("absenceCode"),
                  })}
                />
              </span>
              {getFormErrorMessage("absenceCode")}
            </span>
          </div>
        </div>
        <div className="formgrid grid">
          <div className="field col-4 mt-2">
            <strong className="ml-0">Start Date: </strong>
          </div>
          <div className="field col-8">
            <span className="p-mt-4 p-input-icon-right">
              <span className="p-float-label">
                <Calendar
                  showIcon
                  placeholder="Start Date"
                  type="date"
                  id="startDate"
                  name="startDate"
                  dateFormat="dd/mm/yy"
                  value={formik.values.startDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  disabled={disabled}
                  className={classNames({
                    "p-invalid": isFormFieldValid("startDate"),
                  })}
                />
              </span>
              {getFormErrorMessage("startDate")}
            </span>
          </div>
        </div>

        <div className="formgrid grid">
          <div className="field col-4 mt-2">
            <strong className="ml-0">End Date: </strong>
          </div>
          <div className="field col-8">
            <span className="p-mt-4 p-input-icon-right">
              <span className="p-float-label">
                <Calendar
                  showIcon
                  placeholder="End Date"
                  type="date"
                  id="endDate"
                  name="endDate"
                  dateFormat="dd/mm/yy"
                  value={formik.values.endDate}
                  onChange={formik.handleChange}
                  minDate={new Date(formik.values.startDate)}
                  disabled={disableEndDate}
                  className={classNames({
                    "p-invalid": isFormFieldValid("endDate"),
                  })}
                />
              </span>
              {getFormErrorMessage("endDate")}
            </span>
          </div>
        </div>

        {["ESIN"].includes(region) &&
          ["BL"].includes(formik.values.absenceCode.code) && (
            <>
              <div className="formgrid grid">
                <div className="field col-4 mt-2">
                  <strong className="ml-0">Bereavement Date: </strong>
                </div>
                <div className="field col-8">
                  <span className="p-mt-4 p-input-icon-right">
                    <span className="p-float-label">
                      <Calendar
                        showIcon
                        placeholder="Expiration Date"
                        type="date"
                        id="expirationDate"
                        name="expirationDate"
                        dateFormat="dd/mm/yy"
                        value={formik.values.expirationDate}
                        onChange={formik.handleChange}
                        minDate={new Date(formik.values.startDate)}
                        className={classNames({
                          "p-invalid": isFormFieldValid("expirationDate"),
                        })}
                      />
                    </span>
                    {getFormErrorMessage("expirationDate")}
                  </span>
                </div>
              </div>
            </>
          )}
        {["ESIN"].includes(region) &&
          ["ML", "PL"].includes(formik.values.absenceCode.code) && (
            <>
              <div className="formgrid grid">
                <div className="field col-4 mt-2">
                  <strong className="ml-0">
                    {formik.values.absenceCode.code} Leave Type:{" "}
                  </strong>
                </div>
                <div className="field col-3">
                  <span className="p-mt-4 p-input-icon-right">
                    <span className="p-float-label">
                      <Dropdown
                        id="leaveReason"
                        name="leaveReason"
                        placeholder={`Select ${formik.values.absenceCode.code} Leave Type`}
                        value={formik.values.leaveReason}
                        options={leaveReasons[formik.values?.absenceCode?.code]}
                        onChange={formik.handleChange}
                        className={classNames({
                          "p-invalid": isFormFieldValid("leaveReason"),
                        })}
                      />
                    </span>
                    {getFormErrorMessage("leaveReason")}
                  </span>
                </div>
                {formik.values.absenceCode.code &&
                  ["Delivery"].includes(formik.values.leaveReason) && (
                    <>
                      <div className="formgrid grid col-5 pr-0">
                        <div className="field col-6 mt-2">
                          <strong className="ml-0">No of Child: </strong>
                        </div>
                        <div className="field col-6 pr-0">
                          <span className="p-mt-4 p-input-icon-right">
                            <span className="p-float-label">
                              <Dropdown
                                id="numberOfChild"
                                name="numberOfChild"
                                placeholder={`Select no of child`}
                                value={formik.values.numberOfChild}
                                options={numList}
                                onChange={formik.handleChange}
                                className={classNames({
                                  "p-invalid":
                                    isFormFieldValid("numberOfChild"),
                                })}
                              />
                            </span>
                            {getFormErrorMessage("numberOfChild")}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                {formik.values.absenceCode.code &&
                  ["Adoption"].includes(formik.values.leaveReason) && (
                    <>
                      <div className="formgrid grid col-5 pr-0">
                        <div className="field col-5 mt-2">
                          <strong className="ml-0">Child Age: </strong>
                        </div>
                        <div className="field col-7 pr-0 p-mt-4 ">
                          <div className="p-inputgroup">
                            <InputText
                              id="adoptedChildAge"
                              name="adoptedChildAge"
                              placeholder={`Age`}
                              value={formik.values.adoptedChildAge}
                              onChange={formik.handleChange}
                              className={classNames({
                                "p-invalid":
                                  isFormFieldValid("adoptedChildAge"),
                              })}
                            />
                            <span className="p-inputgroup-addon">Month(s)</span>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
              </div>
              {formik.values.absenceCode.code &&
                ["Adoption"].includes(formik.values.leaveReason) && (
                  <>
                    <div className="formgrid grid">
                      <div className="field col-4 mt-2">
                        <strong className="ml-0">
                          {formik.values.leaveReason} date:{" "}
                        </strong>
                      </div>
                      <div className="field col-8">
                        <span className="p-mt-4 p-input-icon-right">
                          <span className="p-float-label">
                            <Calendar
                              showIcon
                              placeholder="Adoption date"
                              type="date"
                              id="adoptionDate"
                              name="adoptionDate"
                              dateFormat="dd/mm/yy"
                              value={formik.values.adoptionDate}
                              onChange={formik.handleChange}
                              minDate={new Date(formik.values.startDate)}
                              className={classNames({
                                "p-invalid": isFormFieldValid("adoptionDate"),
                              })}
                            />
                          </span>
                          {getFormErrorMessage("adoptionDate")}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              {formik.values.absenceCode.code &&
                ["Miscarriage", "Illness"].includes(
                  formik.values.leaveReason,
                ) && (
                  <>
                    <div className="formgrid grid">
                      <div className="field col-4 mt-2">
                        <strong className="ml-0">
                          {formik.values.leaveReason} date:{" "}
                        </strong>
                      </div>
                      <div className="field col-8">
                        <span className="p-mt-4 p-input-icon-right">
                          <span className="p-float-label">
                            <Calendar
                              showIcon
                              placeholder="Miscarriage date"
                              type="date"
                              id="miscarriageDate"
                              name="miscarriageDate"
                              dateFormat="dd/mm/yy"
                              value={formik.values.miscarriageDate}
                              onChange={formik.handleChange}
                              minDate={new Date(formik.values.startDate)}
                              className={classNames({
                                "p-invalid":
                                  isFormFieldValid("miscarriageDate"),
                              })}
                            />
                          </span>
                          {getFormErrorMessage("miscarriageDate")}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              {formik.values.absenceCode.code &&
                ["Delivery"].includes(formik.values.leaveReason) && (
                  <>
                    <div className="formgrid grid">
                      <div className="field col-4 mt-2">
                        <strong className="ml-0">
                          Expected delivery date:{" "}
                        </strong>
                      </div>
                      <div className="field col-8">
                        <span className="p-mt-4 p-input-icon-right">
                          <span className="p-float-label">
                            <Calendar
                              showIcon
                              placeholder="Expected delivery date"
                              type="date"
                              id="dateOfDelivery"
                              name="dateOfDelivery"
                              dateFormat="dd/mm/yy"
                              value={formik.values.dateOfDelivery}
                              onChange={formik.handleChange}
                              minDate={new Date(formik.values.startDate)}
                              className={classNames({
                                "p-invalid": isFormFieldValid("dateOfDelivery"),
                              })}
                            />
                          </span>
                          {getFormErrorMessage("dateOfDelivery")}
                        </span>
                      </div>
                    </div>
                  </>
                )}
            </>
          )}
        <div className="formgrid grid">
          <div className="field col-4 mt-2">
            <strong className="ml-0">Leave Duration: </strong>
          </div>
          {/* <div className="field col-8">
            <span className="p-mt-4 p-input-icon-right">
              <span className="p-float-label">
                <Dropdown
                  id="leaveType"
                  name="leaveType"
                  placeholder="Select Leave Duration"
                  value={formik.values.leaveType}
                  options={leaveDuration}
                  optionLabel="title"
                  onChange={(e) => {
                    formik.setFieldValue("leaveType", e.value);
                    if (e.value?.code !== "HR") {
                      formik.setFieldValue("leaveHours", "");
                    }
                  }}
                  className={classNames({
                    "p-invalid": isFormFieldValid("leaveType"),
                  })}
                />
              </span>
              {getFormErrorMessage("leaveType")}
            </span>
          </div> */}
          <div className="field col-8">
            {region === "ESAU" ? (
              <div className="p-inputtext p-component p-filled">Hourly</div>
            ) : (
              <span className="p-mt-4 p-input-icon-right">
                <span className="p-float-label">
                  <Dropdown
                    id="leaveType"
                    name="leaveType"
                    placeholder="Select Leave Duration"
                    value={formik.values.leaveType}
                    options={leaveDuration}
                    optionLabel="title"
                    onChange={(e) => {
                      formik.setFieldValue("leaveType", e.value);
                      if (e.value?.code !== "HR") {
                        formik.setFieldValue("leaveHours", "");
                      }
                    }}
                    className={classNames({
                      "p-invalid": isFormFieldValid("leaveType"),
                    })}
                  />
                </span>
                {getFormErrorMessage("leaveType")}
              </span>
            )}
          </div>
        </div>
        {region === "ESAU" && (
          <div className="formgrid grid">
            <div className="field col-4 mt-2">
              <strong className="ml-0">Leave Hours:</strong>
            </div>
            <div className="field col-8">
              <span className="p-float-label">
                <InputText
                  id="leaveHours"
                  name="leaveHours"
                  type="text"
                  inputMode="decimal"
                  pattern="^\d*\.?\d*$"
                  value={formik.values.leaveHours}
                  onChange={formik.handleChange}
                  className={classNames({
                    "p-invalid": isFormFieldValid("leaveHours"),
                  })}
                  placeholder="Enter hours (e.g. 1.5)"
                />
              </span>
              {getFormErrorMessage("leaveHours")}
            </div>
          </div>
        )}
        <div className="formgrid grid">
          <div className="field col-4 mt-2">
            <strong className="">Reason of Leave: </strong>
          </div>
          <div className="field col-8">
            <span className="p-mt-4 p-input-icon-right">
              <span className="p-float-label">
                <InputTextarea
                  id="reasonOfLeave"
                  name="reasonOfLeave"
                  value={formik.values.reasonOfLeave}
                  style={{ resize: "none" }}
                  onChange={formik.handleChange}
                  className={classNames({
                    "p-invalid": isFormFieldValid("reasonOfLeave"),
                  })}
                />
              </span>
              {getFormErrorMessage("reasonOfLeave")}
            </span>
          </div>
        </div>
        {["ESSG"].includes(region) &&
          ["CCL"].includes(formik.values.absenceCode.code) && (
            <>
              <div className="formgrid grid">
                <div className="field col-4 mt-2">
                  <strong className="ml-0">Child Citizenship: </strong>
                </div>
                <div className="field col-8">
                  <Dropdown
                    id="childCitizenship"
                    name="childCitizenship"
                    placeholder="Select Child Citizenship"
                    value={formik.values.childCitizenship}
                    options={["Singapore Citizen", "Non-Singapore Citizen"]}
                    onChange={formik.handleChange}
                    className={classNames({
                      "p-invalid": isFormFieldValid("childCitizenship"),
                    })}
                  />
                  {getFormErrorMessage("childCitizenship")}
                </div>
              </div>
              {formik.values.childCitizenship === "Singapore Citizen" && (
                <div className="formgrid grid">
                  <div className="field col-4 mt-2">
                    <strong className="ml-0">Child Date of Birth: </strong>
                  </div>
                  <div className="field col-8">
                    <Calendar
                      showIcon
                      placeholder="Child Date of Birth"
                      id="childDateOfBirth"
                      name="childDateOfBirth"
                      dateFormat="dd/mm/yy"
                      value={formik.values.childDateOfBirth}
                      onChange={formik.handleChange}
                      maxDate={new Date()}
                      className={classNames({
                        "p-invalid": isFormFieldValid("childDateOfBirth"),
                      })}
                    />
                    {getFormErrorMessage("childDateOfBirth")}
                  </div>
                </div>
              )}
            </>
          )}
      </>
    );
  };

  const onUpload = (e) => {
    if (e.files) {
      setFileObjArray(e.files);
    }
  };

  const onSelect = (e) => {
    if (e.files) {
      setFileObjArray(e.files);
    }
  };

  const renderAttachment = () => {
    return (
      <>
        <div className="formgrid grid">
          <div className="col">
            <FileUpload
              ref={fileRef}
              name="file[]"
              onSelect={onUpload}
              multiple
              accept="image/jpg, image/jpeg, image/png, application/pdf"
              maxFileSize={1000000}
              emptyTemplate={
                <p className="m-0" style={{ minHeight: "200px" }}>
                  Drag and drop files to here to upload.
                </p>
              }
            />
          </div>
        </div>
      </>
    );
  };

  const clearDialogData = () => {
    localStorage.removeItem("clickedDate");
    localStorage.removeItem("absenceData");
    formik.resetForm();
    reload();
    back();
    navigate("/leaves");
  };

  const missingAttendenceDialog = () => (
    <Dialog
      header="Missing Attendance"
      className="custom_popup"
      visible={showMissing}
      onHide={() => setShowMissing(false)}
      style={{ width: "710px" }}
    >
      <h2>
        Total no of missing attendance : <strong>{missing.length}</strong>
      </h2>
      <div className="missing-dates">
        <ul>
          {missing.map((e) => (
            <li className={"D-" + moment(e).format("ddd")}>
              <span>{moment(e).format("ddd DD-MM-YYYY")}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="ess-leave-date-selection grid mt-2">
        <div className="col formgrid grid display_content">
          <div className="field col-4 mt-2 mb-0">
            <strong className="ml-0">Start Date: </strong>
          </div>
          <div className="field col-8 mb-0">
            <span className="p-mt-4 p-input-icon-right">
              <span className="p-float-label">
                <Calendar
                  showIcon
                  placeholder="Start Date"
                  type="date"
                  id="startDate"
                  name="startDate"
                  dateFormat="dd/mm/yy"
                  value={formik.values.startDate}
                  onChange={(e) => handleDateChange(e.target.value)}
                  disabled={disabled}
                  className={classNames({
                    "p-invalid": isFormFieldValid("startDate"),
                  })}
                />
              </span>
              {getFormErrorMessage("startDate")}
            </span>
          </div>
        </div>
        <div className="col formgrid grid">
          <div className="field col-4 mt-2 mb-0">
            <strong className="ml-0">End Date: </strong>
          </div>
          <div className="field col-8 mb-0">
            <span className="p-mt-4 p-input-icon-right">
              <span className="p-float-label">
                <Calendar
                  showIcon
                  placeholder="End Date"
                  type="date"
                  id="endDate"
                  name="endDate"
                  dateFormat="dd/mm/yy"
                  value={formik.values.endDate}
                  onChange={formik.handleChange}
                  minDate={new Date(formik.values.startDate)}
                  className={classNames({
                    "p-invalid": isFormFieldValid("endDate"),
                  })}
                />
              </span>
              {getFormErrorMessage("endDate")}
            </span>
          </div>
        </div>
      </div>
      <div style={{ textAlign: "center" }}>
        <hr />
        <Button
          label="OK"
          icon="pi pi-check"
          className="mr-1"
          style={{ width: "150px" }}
          onClick={() => setShowMissing(false)}
        />
        {/* <Button label="Cancel" icon='pi pi-times' className='p-button-danger' style={{ width: '150px' }} /> */}
      </div>
    </Dialog>
  );

  return (
    <div className="ess-page new-absence-page">
      <div className="grid">
        {/* <a href="#" className="ess-icon-link" onClick={() => clearDialogData()}><i className="pi pi-arrow-circle-left"></i></a> */}
        <div className="col-fixed absence_title">
          {!dialog && <h2 className="mb-0 mt-2">New Absence Request</h2>}
          <p className="header-desc mt-2">
            User can allow here to create new absence request
          </p>
        </div>
        <div className="col pt-5" style={{ textAlign: "right" }}>
          {!dialog && (
            <Button
              className="p-button mr-1 mb-2"
              onClick={clearDialogData}
              type="button"
              icon="pi pi-angle-left"
              label="My Leaves Dashboard"
            />
          )}
          <Button
            label="Missing Attendance"
            className="p-button-danger mb-2"
            onClick={() => setShowMissing(true)}
          />
        </div>
      </div>
      <div className="ess-leave-form ess-border">
        <form onSubmit={formik.handleSubmit} className="p-fluid p-grid">
          <div className="p-3 pb-0">
            <div className="grid">
              <div className="col-12 md:col-7 pl-4 pr-4">
                {renderNewLeaveRequest()}
              </div>
              <div className="col-12 md:col-5 pr-4 pl-4">
                {renderAttachment()}
              </div>
            </div>
            <hr />
            <div className="grid">
              <div className="col">
                <div className="formgrid grid">
                  <div className="field col-12 m-0">
                    <Button
                      style={{ width: "100px", float: "right" }}
                      className="p-button-secondary"
                      type="button"
                      label="Cancel"
                      onClick={() => clearDialogData()}
                    />
                    <Button
                      style={{ width: "100px", float: "right" }}
                      className="p-button-primary mr-2"
                      type="submit"
                      label="Submit"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* {serverMsg && <p className="ess-server-msg">
                    {serverMsg}
                </p>} */}
        </form>
      </div>
      <div className="mt-3 ess-table ess-border p-3">
        {leaves?.length > 0 && (
          <h3 className="p-1 mb-0 text-center danger">
            You have {leaves?.length} leave request(s) saved as draft. Please
            review and submit them to ensure your leaves are applied
            successfully.
          </h3>
        )}
        <Draft leaves={leaves || []} reload={reload} />
      </div>
      {missingAttendenceDialog()}
    </div>
  );
};

export default NewAbsenceRequest;
